import { AlertTriangle } from 'lucide-react';
import { useT } from '@/core/i18n';
import { errorMessage } from '@/core/errorMessage';
import { Card } from './ui';
import { useSession } from '@/core/session';

/* =============================================================================
 * Why there is nothing here
 * -----------------------------------------------------------------------------
 * A query that fails hands back `undefined`, and a screen written as
 *
 *     rows.length === 0 ? <EmptyState /> : <Table />
 *
 * renders "nothing yet" for it. Which is how a payroll screen tells somebody
 * they have no payroll when what actually happened was a refused permission, a
 * column the database does not have, or a dropped connection.
 *
 * The two states look identical and mean opposite things. One is a new company
 * with nothing in it; the other is a month of wages that is still there and
 * cannot be read. Somebody seeing the second and being told the first reasonably
 * concludes their data is gone.
 *
 * So an error gets said. In the words the database used, because a person
 * relaying it to whoever can fix it needs the actual message and not a
 * paraphrase of it.
 * ========================================================================== */

export function QueryError({ error, what }: { error: unknown; what?: string }) {
  const t = useT();
  const { platform } = useSession();
  if (!error) return null;

  /* Not `error instanceof Error ? error.message : String(error)`, which is what
     this said and which produced the literal text "[object Object]" on the one
     kind of failure this component exists for.

     supabase-js rejects with a PostgrestError: a plain object carrying message,
     details, hint and code. It has a message, but it does not inherit from
     Error, so instanceof is false and the whole thing falls through to
     String(), which on a plain object is that phrase. Every refused permission
     and every missing column in the application was being reported as
     [object Object] — and this is the component whose entire purpose is to say
     what went wrong instead of showing an empty screen. */
  const message = errorMessage(error);

  /* The two that have an answer somebody can act on, told apart by code rather
     than by reading the prose — PostgREST's wording is not a contract. */
  /* For the platform operator only. The missing-column hint is a migration
     command and the permission one names the database; a company already has
     the sentence above and a reference to quote, and the detail went to the
     platform through errorMessage. Showing a company "run npm run db:pending"
     is the thing this whole app has agreed not to do. */
  const code = (error as { code?: string })?.code;
  const hint = !platform ? null
    : code === '42703' ? t('qe.missingColumn')
      : code === '42501' || code === 'PGRST301' ? t('qe.notPermitted')
        : null;

  return (
    <Card>
      <div className="flex items-start gap-2.5">
        <AlertTriangle size={17} className="text-danger shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-danger">
            {what ?? t('qe.couldNotBeRead')}
          </p>

          {/* Not paraphrased. This is the line somebody reads out to whoever
              can fix it, and a friendlier version of it is a worse one. */}
          <p className="text-[12.5px] text-muted mt-1 leading-snug break-words">
            {message}
          </p>

          {hint && (
            <p className="text-[12px] text-subtle mt-2 leading-snug">{hint}</p>
          )}

          <p className="text-[12px] text-subtle mt-2 leading-snug">
            {t('qe.nothingLost')}
          </p>
        </div>
      </div>
    </Card>
  );
}
